import { Controller } from "@hotwired/stimulus"

// Multi-select filter dropdown controller
export default class extends Controller {
  static targets = ["button", "dropdown", "checkbox", "buttonText", "chevron", "hiddenInputs", "optionsContainer"]
  static values = { name: String }

  connect() {
    // Close dropdown when clicking outside
    this.boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this)
    document.addEventListener("click", this.boundCloseOnOutsideClick)
    
    // Initialize checkboxes based on existing values
    this.initializeFromExistingValues()
    
    this.updateButtonText()
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnOutsideClick)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.dropdownTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.dropdownTarget.classList.remove("hidden")
    this.chevronTarget.style.transform = "rotate(180deg)"
  }

  close() {
    this.dropdownTarget.classList.add("hidden")
    this.chevronTarget.style.transform = "rotate(0deg)"
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  selectAll(event) {
    event.preventDefault()
    event.stopPropagation()
    
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = true
    })
    
    this.updateSelection()
  }

  clearAll(event) {
    event.preventDefault()
    event.stopPropagation()
    
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })
    
    this.updateSelection()
  }

  updateSelection() {
    // Update hidden inputs
    this.updateHiddenInputs()
    
    // Update button text
    this.updateButtonText()
    
    // Notify parent form that a change occurred (for manual filter submission)
    const form = this.element.closest('form')
    if (form) {
      const event = new CustomEvent('multi-select:change', {
        bubbles: true,
        detail: { 
          name: this.nameValue, 
          values: this.getSelectedValues() 
        }
      })
      form.dispatchEvent(event)
    }
  }

  updateHiddenInputs() {
    const selectedValues = this.getSelectedValues()
    const container = this.hiddenInputsTarget
    
    // Clear existing hidden inputs
    container.innerHTML = ''
    
    // Add single hidden input with comma-separated values for filter compatibility
    if (selectedValues.length > 0) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = this.nameValue
      input.value = selectedValues.join(',')
      container.appendChild(input)
    }
  }

  updateButtonText() {
    const selectedCount = this.getSelectedValues().length
    const totalCount = this.checkboxTargets.length
    
    if (selectedCount === 0) {
      this.buttonTextTarget.textContent = `All ${this.getFieldLabel()}`
      this.buttonTextTarget.className = "text-gray-500"
    } else if (selectedCount === totalCount) {
      this.buttonTextTarget.textContent = `All ${this.getFieldLabel()}`
      this.buttonTextTarget.className = "text-gray-900 font-medium"
    } else {
      this.buttonTextTarget.textContent = `${selectedCount} ${selectedCount === 1 ? 'item' : 'items'} selected`
      this.buttonTextTarget.className = "text-gray-900 font-medium"
    }
  }

  getSelectedValues() {
    return this.checkboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)
  }

  getFieldLabel() {
    const label = this.element.querySelector('label')
    return label ? label.textContent.trim() : 'Items'
  }

  initializeFromExistingValues() {
    // Look for existing hidden input with comma-separated values
    const existingInput = this.element.querySelector(`input[name="${this.nameValue}"]`)
    if (existingInput && existingInput.value) {
      const selectedValues = existingInput.value.split(',').map(v => v.trim())
      
      this.checkboxTargets.forEach(checkbox => {
        checkbox.checked = selectedValues.includes(checkbox.value)
      })
      
      // Update hidden inputs to reflect current state
      this.updateHiddenInputs()
    }
  }
}